/**
 * Tutorial System - Data Definitions
 *
 * Tutorials trigger on first-time actions and guide new players.
 * Checks for existing save data to skip tutorials for returning players.
 */

export const TUTORIALS = {
  // 1. Game Start - Welcome message
  gameStart: {
    id: 'gameStart',
    title: 'Welcome! 👑',
    description: 'Build your kingdom from Stone Age through 11 eras to Transcendence. Your goal: construct the **Dimensional Beacon** wonder.\n\n**Core Loop**: Buildings produce resources → Research unlocks new things → Quests guide your progress → World map has tiles to explore, gather, and build on.\n\nDungeons and Encounters drop gear, materials, and XP. Star Force & Forging enhance equipment. Game auto-saves every 30 seconds.',
    position: 'center',
    targetElement: null,
    priority: 1,
    trigger: 'onGameLoad'
  },

  // 3. First Building Purchase
  firstBuilding: {
    id: 'firstBuilding',
    title: 'Buildings 🏗️',
    description: 'Buildings auto-produce resources every second. Each one costs more than the last, so plan your spending.\n\nYou can specialize buildings later for +50% output. Early tip: build 5-10 Woodcutters, Farms, and Stone Pickers to get your economy going.',
    position: 'center-left',
    targetElement: '.building-card',
    priority: 3,
    trigger: 'onFirstBuildingPurchase'
  },

  // 4. First Tech Research
  firstResearch: {
    id: 'firstResearch',
    title: 'Research 📜',
    description: 'Research unlocks new buildings, resources, and advisors. Your kingdom\'s era is based on your highest researched tech tier.\n\nThe queue holds up to 5 techs and auto-advances. Click "🌳 Tech Tree" to see everything and plan ahead.',
    position: 'right',
    targetElement: '.tech-panel',
    priority: 4,
    trigger: 'onFirstResearchStart'
  },

  // 5. First Quest
  firstQuest: {
    id: 'firstQuest',
    title: 'Quests 📋',
    description: 'Quests guide your progress and give big rewards. There are 100+ across 10 chapters, and they auto-advance when you finish.\n\nClick on objectives to jump to the right panel. Later chapter rewards scale up significantly!',
    position: 'right',
    targetElement: '.quest-panel',
    priority: 5,
    trigger: 'onFirstQuestComplete'
  },

  // 7. First Map Movement
  firstMove: {
    id: 'firstMove',
    title: 'World Map 🗺️',
    description: 'Click adjacent tiles (gold border) to explore. There are 50+ biome types with different resources.\n\n**Exploration**: Start with 60 tiles (expandable via tech). Double-click discovered tiles to auto-path. First 10 tiles guarantee wood, stone, food, and knowledge access.\n\n**Points of Interest**: Dungeons (purple border), Encounters (⛺🐺🕷️), Teleport Beacons (🔮), and Wonder Sites (glowing triangles).',
    position: 'center',
    targetElement: '.wm-canvas',
    priority: 7,
    trigger: 'onFirstMove'
  },

  // 8. First Gather from Tile
  firstGather: {
    id: 'firstGather',
    title: 'Gathering 🌾',
    description: 'Click "Gather" to collect resources from tiles. No cooldown - click as fast as you want!\n\nToggle ▶️ for auto-gather (collects every 0.5s). Tile quality and your era boost yields. Enable auto-gather on 5-10 high-quality tiles for passive income.',
    position: 'bottom',
    targetElement: '.wm-gather-buttons',
    priority: 8,
    trigger: 'onFirstGather'
  },

  // 9. First Expedition
  firstExpedition: {
    id: 'firstExpedition',
    title: 'Expeditions 🏕️',
    description: 'Expeditions multiply gather yield massively (×20 to ×550). They work offline and take time to complete.\n\n**Bonuses**: 40% chance for XP (5-25 based on duration), 20% chance to DOUBLE all resources!\n\nPick a duration (x20, x40, etc), then click "Start Expedition". Canceling = zero rewards. Target high-quality tiles (110%+) for best results.',
    position: 'center',
    targetElement: null,
    priority: 9,
    trigger: 'onFirstExpeditionClick'
  },

  // 9b. First Tile Build
  firstTileBuild: {
    id: 'firstTileBuild',
    title: 'Building on Tiles 🏗️',
    description: 'Tile buildings cost 2× the normal price but produce resources automatically. Build on high-quality tiles (110%+) for best efficiency.\n\n**Important**: Building on a tile **unlocks** that building for mass production in the Buildings Panel! Discover → Build on tile → Unlocked for panel.',
    position: 'center',
    targetElement: null,
    priority: 9.5,
    trigger: 'onFirstTileBuild'
  },

  // 10. Advisors Unlock
  advisorsUnlock: {
    id: 'advisorsUnlock',
    title: 'Advisors 👑',
    description: 'Advisors provide powerful bonuses but come with penalties. You start with 2 slots (expandable to 5).\n\n**Trade-offs**: +50% gold might mean -20% food. Some have special abilities like reduced building costs, faster research, or event resistance.\n\n**Strategy**: Match advisors to your current needs. Fire and rehire as your priorities change!',
    position: 'top',
    targetElement: '.nav-btn.advisors',
    priority: 10,
    trigger: 'onFirstAdvisorUnlock'
  },

  // 11. First Monument
  firstMonument: {
    id: 'firstMonument',
    title: 'Monuments 🏛️',
    description: 'Monuments are expensive mega-projects with massive permanent bonuses. Construction continues even when offline!\n\n**Two Types**:\n• **Wonders** - Build in the Monuments tab; some require discovering Wonder Sites on the map first\n• **Great Works** - Must be placed on specific map tile clusters (e.g., 3+ nature tiles nearby)\n\nCancel refunds 50%. One construction at a time per type.',
    position: 'center',
    targetElement: '.center-tab[data-tab="monuments"]',
    priority: 11,
    trigger: 'onFirstMonumentStart'
  },

  // 12. Building Specialization
  buildingSpec: {
    id: 'buildingSpec',
    title: 'Specializations ⚡',
    description: 'Specialize buildings to boost their primary output by **+50%**! Click the ⚡ button on any building card.\n\n**Upgrade Paths**: Each building type has different specialization options. Some focus on raw output, others on efficiency or secondary resources.\n\n**Cost**: Specializations require resources. Higher tier buildings have more powerful options.',
    position: 'center',
    targetElement: '.building-card .spec-btn',
    priority: 12,
    trigger: 'onFirstSpecialization'
  },

  // 13. Age Advancement
  ageAdvance: {
    id: 'ageAdvance',
    title: 'Ages 🏆',
    description: 'Your era is determined by your **highest researched tech tier**, not building count!\n\n**11 Ages**: Stone Age → Bronze → Iron → Classical → Medieval → Renaissance → Industrial → Modern → Atomic → Information → Transcendence.\n\n**Benefits**: Higher eras increase gather yields, unlock new biomes, dungeons, encounters, and more. Check the tech tree to plan your path!',
    position: 'top-center',
    targetElement: '.age-progress-bar',
    priority: 13,
    trigger: 'onFirstAgeAdvance'
  },

  // 14. Stats & Progress
  statsPanel: {
    id: 'statsPanel',
    title: 'Stats & Progress 📊',
    description: 'Track your kingdom\'s growth and achievements!\n\n**Tracked**: Total resources gathered, buildings owned, techs researched, quests completed, tiles explored, dungeons cleared, encounters completed, and more.\n\n**Production Breakdown**: See exactly where your resources come from - buildings, tile production, bonuses, and penalties all itemized.',
    position: 'top',
    targetElement: '.nav-btn.stats',
    priority: 15,
    trigger: 'onFirstStatsOpen'
  },

  // 16. Buildings Tab
  buildingsTab: {
    id: 'buildingsTab',
    title: 'Buildings Tab 🏗️',
    description: 'Buildings must be **discovered** and **built on the map** before you can mass-produce them here!\n\n**Flow**: Explore biomes to discover buildings → Build it once on a map tile (costs 2×) → Now unlocked to build as many as you want here!\n\nHover over buildings to see their discovery/unlock status.',
    position: 'center',
    targetElement: '.center-tab[data-tab="buildings"]',
    priority: 16,
    trigger: 'onFirstBuildingsTabOpen'
  },

  // 17. Monuments Tab
  monumentsTab: {
    id: 'monumentsTab',
    title: 'Monuments Tab 🏛️',
    description: 'Monuments are huge projects that give permanent bonuses to your whole kingdom. Expensive and time-consuming, but worth it.\n\n**Wonders**: Build directly here; some need Wonder Sites discovered first.\n**Great Works**: Must be placed on specific map tile clusters.\n\nOne construction per type at a time. Choose wisely!',
    position: 'center',
    targetElement: '.center-tab[data-tab="monuments"]',
    priority: 17,
    trigger: 'onFirstMonumentsTabOpen'
  },

  // 18. Advisors Button
  advisorsButton: {
    id: 'advisorsButton',
    title: 'Advisors Panel 👑',
    description: 'Advisors give powerful bonuses but come with trade-offs. You start with 2 slots and can expand to 5.\n\nEach advisor boosts some resources while reducing others. Pick ones that match your strategy and don\'t hurt what you need most.',
    position: 'center',
    targetElement: null,
    priority: 18,
    trigger: 'onFirstAdvisorsButtonClick'
  },

  // 19. Character Tab (Multi-page)
  characterTab: {
    id: 'characterTab',
    title: 'Character & Combat ⚔️',
    pages: [
      // Page 1: Overview
      '**Character System**\n\nYour character explores dungeons and encounters to fight monsters. You have 4 primary stats: STR, DEX, INT, and LUK.\n\nThese stats determine HP, Attack, Defense, Crit Chance, and more. Earn stat points when leveling up and allocate them as you like!',

      // Page 2: Equipment
      '**Equipment**\n\n12 slots: Weapon, Offhand, Helmet, Cape, Top, Bottom, Gloves, Boots, Belt, Pendant, and 2 Rings.\n\nQuality (50%-125%) scales stats. 100% quality = 2× base stats! **6 Rarities**: Common → Uncommon → Rare → Epic → Legendary → Mythic.\n\nRight-click equipment for **Star Force** (enhance with ✦) or **Forging** (apply scrolls with gemstones).',

      // Page 3: Dungeons vs Encounters
      '**Dungeons vs Encounters**\n\n**Dungeons** (purple border): 5 tiers from Ancient Ruins (Lv5) to Void Citadel (Lv100). Level requirements, bosses, full loot, 10-50min cooldowns.\n\n**Encounters** (⛺🐺🕷️): No level requirements! Enemies scale to your level. 75% XP, 50% material drops, 5% equipment chance. 1min cooldowns. Great for leveling!',

      // Page 4: Combat & Strategy
      '**Combat**\n\nCombat is automatic. Damage = (Attack - Enemy Defense × 0.5) × Crit Multiplier.\n\n**Encounters**: Perfect for leveling. No requirements, enemies scale to you.\n**Dungeons**: Better loot but need proper gear and level.\n\nDeath = partial loot, no penalties. Use potions and buffs from Quick Use panel!'
    ],
    position: 'center',
    targetElement: '.center-tab[data-tab="character"]',
    priority: 19,
    trigger: 'onFirstCharacterTabOpen'
  },

  // 20. Trade Routes
  tradeRoutes: {
    id: 'tradeRoutes',
    title: 'Trade Routes 🛤️',
    description: 'Connect tiles to your Kingdom for production bonuses! Click the 🛤️ button on the world map to toggle the trade route overlay.\n\n**How it Works**:\n• Build route segments on tiles adjacent to existing routes\n• Routes must connect back to your Kingdom (0,0)\n• Connected tiles get production bonuses\n\n**Costs**: Route segments cost wood, stone, and gold. Cost increases with distance. Research techs to reduce costs!',
    position: 'center',
    targetElement: '.wm-trade-route-bar',
    priority: 20,
    trigger: 'onFirstTradeRouteToggle'
  },

  // 21. Dungeon Entry
  dungeonEntry: {
    id: 'dungeonEntry',
    title: 'Dungeon Found 🏚️',
    description: 'You found a dungeon! Click to open the entry modal and see details.\n\n**Before Entering**:\n• Check recommended level vs your character level\n• Too low? Try **Encounters** first to level up!\n• Note the cooldown time (10-50min based on tier)\n\n**Inside**: Move by clicking adjacent tiles. Combat is automatic. Defeat the boss to complete! Death = partial loot, exit early = keep what you found.',
    position: 'center',
    targetElement: null,
    priority: 21,
    trigger: 'onFirstDungeonTileClick'
  },

  // 22. Consumables
  consumables: {
    id: 'consumables',
    title: 'Consumables & Buffs 🧪',
    description: 'Potions and scrolls provide temporary boosts! Double-click consumables in your inventory to use them.\n\n**Types**:\n• **Health Potions** - Restore HP (use in dungeons!)\n• **Stat Potions** - Temporary ATK, DEF, SPD boosts\n• **Scrolls** - Max HP, Crit Chance buffs\n• **Blessings** - XP and Loot Quality bonuses\n\nActive buffs show timers in the Character panel. Use the Quick Use panel during combat!',
    position: 'center',
    targetElement: null,
    priority: 22,
    trigger: 'onFirstConsumableUse'
  },

  // 23. Encounters
  encounterEntry: {
    id: 'encounterEntry',
    title: 'Encounters ⛺',
    description: 'Encounters are mini combat zones perfect for leveling up!\n\n**Key Features**:\n• **No level requirements** - Anyone can enter\n• **Enemies scale to your level** - Always a fair fight\n• **Quick cooldowns** - Only 1 minute between runs\n• **5×5 grid** - Smaller than dungeons, faster to clear\n\n**Trade-offs**: 75% XP, 50% material drops, 5% equipment chance, no boss. Great for grinding XP and materials!',
    position: 'center',
    targetElement: null,
    priority: 23,
    trigger: 'onFirstEncounterTileClick'
  },

  // 24. Star Force Enhancement
  starForce: {
    id: 'starForce',
    title: 'Star Force ✦',
    description: 'Enhance equipment with stars for bonus stats! Right-click any equipment and select "Enhance".\n\n**How it Works**:\n• Max 15 stars per item\n• Costs **Star Essence (✦)** dropped by dungeon/encounter mobs\n• Success rates: 95% at 0★ down to 25% at 14★\n• Fail at 6+ stars = drop 1 star (minimum 5★)\n\n**Pity System**: +10% success per consecutive fail (max +30%)\n**Safeguard**: 2× cost at stars 6-10 to prevent downgrade\n**Milestones**: 5★, 10★, 15★ give bonus crit and stats!',
    position: 'center',
    targetElement: null,
    priority: 24,
    trigger: 'onFirstStarForceOpen'
  },

  // 25. Forging (Scrolling) System
  forging: {
    id: 'forging',
    title: 'Forging (Scrolls) 📜',
    description: 'Apply scrolls to equipment for permanent stat boosts! Right-click equipment and select "Forge".\n\n**Scroll Slots**: Equipment has limited slots (5-10 based on type)\n**Gemstones**: 5 colors dropped by mobs - Ruby (ATK), Sapphire (DEF), Emerald (HP), Topaz (Crit), Amethyst (SPD)\n\n**Gem Tiers**: Tier 1 (90% success, +1 stat) to Tier 5 (15% success, +12 stat)\n\n**Warning**: Failed scrolls permanently destroy that slot! Higher tier = better stats but riskier.',
    position: 'center',
    targetElement: null,
    priority: 25,
    trigger: 'onFirstForgingOpen'
  },

  // 26. Forge Tab
  forgeTab: {
    id: 'forgeTab',
    title: 'Forge Tab ⚒️',
    description: 'The Forge has two enhancement systems:\n\n**Star Force (✦)**: Enhance items with stars for scaling bonuses. Uses Star Essence from dungeons/encounters.\n\n**Forging (Scrolls)**: Apply scrolls with gemstones for permanent flat stats. Each equipment has limited scroll slots.\n\n**Clean Slate**: Reset forged equipment (removes all scrolls) to try again with better scrolls later.\n\nBoth systems stack - max out stars AND scrolls for best gear!',
    position: 'center',
    targetElement: '.center-tab[data-tab="forge"]',
    priority: 26,
    trigger: 'onFirstForgeTabOpen'
  },

  // 27. Wonder Sites
  wonderSites: {
    id: 'wonderSites',
    title: 'Wonder Sites 🗿',
    description: 'Wonder Sites are special 3-tile triangular formations on the map. They glow with unique colors!\n\n**How They Work**:\n• Discovering any 1 tile reveals all 3 tiles in the site\n• Some Wonders can ONLY be built after finding their site\n• Sites spawn at least 10 tiles from your Kingdom\n\n**Site-Bound Wonders**: Stonehenge, Hanging Gardens, Great Library, and more require site discovery first. Check the Monuments tab to see which!',
    position: 'center',
    targetElement: null,
    priority: 27,
    trigger: 'onFirstWonderSiteDiscovery'
  },

  // 28. Great Works
  greatWorks: {
    id: 'greatWorks',
    title: 'Great Works 🗺️',
    description: 'Great Works are map-bound monuments that require specific tile clusters to build.\n\n**Placement Requirements**:\n• Each Great Work needs an "anchor" tile of a specific biome category\n• Nearby tiles must match criteria (e.g., 3+ nature tiles within range)\n• Categories: Nature, Stone, Water, Knowledge, Magical, Hostile, Exotic\n\n**Building**: Select a Great Work in Monuments tab → Click "Place" → Valid tiles glow purple → Click to place\n\n**Max 5** Great Works total. Choose locations wisely!',
    position: 'center',
    targetElement: null,
    priority: 28,
    trigger: 'onFirstGreatWorkPlacement'
  },

  // 29. Outposts (Multi-page comprehensive guide)
  outposts: {
    id: 'outposts',
    title: 'Outposts 🏰',
    pages: [
      // Page 1: Overview
      '**Outpost Overview**\n\nOutposts are player-built settlements on world map tiles. Think of them as mini-bases!\n\n**Key Benefits**:\n• **4 Building Slots** - Each grants +5 panel capacity (vs 1 slot on regular tiles)\n• **Mercenary Hiring** - Recruit warriors to auto-run dungeons\n• **Instant Teleport** - Travel here from anywhere on the map\n• **Trade Route Hub** - Connect back to your Kingdom for bonuses\n\n**Max 3 Outposts** (expandable via future techs)',

      // Page 2: Construction & Costs
      '**Building an Outpost**\n\n**Construction Costs** vary by biome:\n• **Tier 1** (Forest/Plains): ~200 wood, 100-150 stone, 50 gold\n• **Tier 2** (Mountains/Coast): ~300 resources, 100-150 gold\n• **Tier 3+** (Ruins/Volcanic): 400+ resources, 150-200 gold\n\n**Construction Time**: Works offline, 30 seconds in dev mode / 5 minutes normal\n\n**Where to Build**: Any discovered tile EXCEPT Kingdom, Dungeons, Encounters. Build near dungeons for efficient merc farming!',

      // Page 3: Building Slots & Capacity
      '**Building Slots**\n\nEach outpost has **4 building slots** (regular tiles only have 1).\n\n**How Capacity Works**:\n• Each building placed = +5 capacity in the Buildings Panel\n• Example: 2 Lumber Mills in outpost = can buy 10 more in panel\n• Outpost buildings count the same as tile buildings\n\n**Strategy**: Build production buildings here to dramatically expand your panel capacity. One outpost = +20 potential capacity!',

      // Page 4: Mercenary System
      '**Mercenaries**\n\nHire mercs to auto-run dungeons while you\'re away!\n\n**4 Classes**:\n• ⚔️ **Warrior** - High HP, balanced defense (tank)\n• 🏹 **Ranger** - High DEX, good crit (DPS)\n• 🔮 **Mage** - High INT, low HP (magic damage)\n• 🗡️ **Rogue** - High LUK, highest crit (burst)\n\n**Quality Rating**: 0.6x-1.4x stat multiplier (like equipment quality)\n• ★☆☆☆☆ Poor | ★★★☆☆ Good | ★★★★★ Legendary\n\n**Max 6 mercenaries per outpost**',

      // Page 5: Squad Missions
      '**Squad Missions**\n\nForm squads (1-4 mercs) to run discovered dungeons automatically.\n\n**Mission Flow**:\n1. Select idle mercs → Create Squad\n2. Choose dungeon tier from dropdown\n3. View success rate (based on squad power)\n4. Start Mission → Wait for completion → Collect rewards\n\n**Rewards**: 70% of manual dungeon loot (gold, XP)\n**Duration**: Scales with dungeon tier (5-20 min)',

      // Page 6: Risk & Permadeath Warning
      '**⚠️ Mission Risks**\n\n**Failed missions have consequences!**\n\n• **30% death chance** per merc on failed missions\n• **Permadeath** - Dead mercs are GONE forever\n• Partial rewards still granted on failure\n\n**Success Rate**:\n• Based on Squad Power vs Recommended Power\n• Minimum 10%, Maximum 95%\n• Stronger squad = better odds\n\n**Tip**: Don\'t send weak squads to hard dungeons! Start with easier tiers to minimize casualties.',

      // Page 7: Trade Routes & Teleportation
      '**Trade & Teleport**\n\n**Trade Route Connection**:\n• Build route segments to connect outpost to your Kingdom\n• Connected outposts get production bonuses\n• View status in the Buildings tab of Outpost Modal\n\n**Instant Teleport**:\n• Completed outposts work as teleport waypoints\n• Use the teleport UI on the world map to jump instantly\n• Great for reaching distant dungeons!\n\n**Tip**: Build outposts near dungeon clusters for efficient merc farming!'
    ],
    position: 'center',
    targetElement: null,
    priority: 29,
    trigger: 'onFirstOutpostCreate'
  },

  // 30. Tower Entry
  towerEntry: {
    id: 'towerEntry',
    title: 'Tower Found 🗼',
    description: 'You found a Tower! Towers are multi-floor dungeons with vertical progression.\n\n**Tower Features**:\n• **Multiple Floors** - 3, 5, or 10 floors depending on tier\n• **Stairs** - Navigate up/down between floors\n• **Checkpoints** - Rest (+50% HP) or exit safely with loot\n• **Boss on Final Floor** - Only the last floor has a boss\n\n**Floor State**: Progress is saved! Going back up remembers which enemies you defeated. Cooldowns range from 15-90 minutes based on tier.',
    position: 'center',
    targetElement: null,
    priority: 30,
    trigger: 'onFirstTowerTileClick'
  }
};

/**
 * Tutorial configuration
 */
export const TUTORIAL_CONFIG = {
  // Don't show tutorials if player has existing save data
  skipIfExistingSave: true,

  // Animation durations
  fadeInDuration: 300, // ms
  fadeOutDuration: 200, // ms

  // Overlay settings (no longer used - kept for backwards compatibility)
  overlayColor: 'rgba(0, 0, 0, 0.7)',
  spotlightPadding: 12, // px around highlighted element

  // Tutorial card settings
  cardMaxWidth: 400, // px
  arrowSize: 12, // px

  // Auto-advance settings
  autoAdvance: false, // Don't auto-advance tutorials
  autoAdvanceDelay: 5000 // ms (if enabled)
};

/**
 * Get tutorial by ID
 */
export function getTutorial(id) {
  return TUTORIALS[id] || null;
}

/**
 * Get all tutorials sorted by priority
 */
export function getAllTutorials() {
  return Object.values(TUTORIALS).sort((a, b) => a.priority - b.priority);
}

/**
 * Check if tutorials should be skipped (existing save detected)
 */
export function shouldSkipTutorials(gameState) {
  if (!TUTORIAL_CONFIG.skipIfExistingSave) return false;

  // Check for indicators of existing progress
  const hasProgress =
    (gameState.buildingCounts && Object.keys(gameState.buildingCounts).length > 0) ||
    (gameState.researchedTechs && gameState.researchedTechs.length > 0) ||
    (gameState.completedQuests && gameState.completedQuests.length > 0) ||
    (gameState.stats && gameState.stats.totalClicks > 10);

  return hasProgress;
}
